import { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faArrowRight,
  faBookOpen,
  faBuilding,
  faCalendar,
  faCheck,
  faCircleQuestion,
  faEnvelope,
  faHandshake,
  faLock,
  faMoon,
  faPhone,
  faShieldCheck,
  faSliders,
  faTriangleExclamation,
} from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../../components/icon/Icon'
import { getPublicQuickPassSharePage } from '../../_data/share-page'
import { DEMO_SHARE_PAYLOAD } from './demo-fixture'
import {
  buildInitials,
  formatAuthProvider,
  formatBathrooms,
  formatBedrooms,
  formatBudget,
  formatDwellingScope,
  formatIncome,
  formatMemberSince,
  formatMoveInDate,
  formatPropertyTypes,
  formatVerifiedDate,
  getVerificationProof,
  buildVerificationContactHref,
  VERIFICATIONS_CONTACT_EMAIL,
  buildPassNumber,
} from './helpers'
import { PublicSharePageState, VerificationProof } from './types'
import {
  AboutGroup,
  AboutParagraph,
  AboutSubtitle,
  Avatar,
  Badge,
  BadgeRow,
  BrandRow,
  BrandTagline,
  BrandWordmark,
  ContactLine,
  ContactRow,
  CustomNote,
  FooterText,
  Headline,
  HeroCard,
  HeroHeader,
  HeroBrand,
  HeroNo,
  HeroBody,
  HeroText,
  HeroTop,
  Perf,
  HeroStub,
  HeroStubRow,
  Barcode,
  TabBar,
  Tab,
  WhatItem,
  WhatIcon,
  WhatText,
  WhatTitle,
  WhatBody,
  WhatCtaRow,
  WhatCta,
  InfoBadge,
  Name,
  PageInner,
  PageRoot,
  SectionCard,
  SectionTitle,
  StatGrid,
  StateBody,
  StateCard,
  StateEmoji,
  StateTitle,
  StatItem,
  StatLabel,
  StatValue,
  VerifyCheck,
  VerifyHead,
  VerifyHeadText,
  VerifyIcon,
  VerifyIntro,
  VerifyItem,
  VerifyList,
  VerifyMethod,
  VerifyPrivacy,
  VerifyProves,
  VerifyProvider,
  VerifyProviderBlurb,
  VerifyProviderName,
  VerifyResult,
  VerifyTitle,
  VerifyTitleRow,
  ConfirmCard,
  ConfirmTitle,
  ConfirmLead,
  ConfirmButton,
  ConfirmHint,
  ConfirmSteps,
  ConfirmStep,
  ConfirmStepNum,
  ConfirmStepText,
  ConfirmStepTitle,
  ConfirmStepBody,
} from './styled'

const initialiseState = (): PublicSharePageState => ({
  status: 'loading',
  payload: null,
  errorMessage: '',
})

/**
 * Public, guest-accessible renter "pass" page at `/me/:slug?k=token`.
 * Renters share this URL with property managers / landlords. Token gates
 * access; visibility flags on the share-page doc gate which sections
 * render. Increments view-count on every load via the backend.
 *
 * @returns {JSX.Element}
 */
const PublicSharePage = (): JSX.Element => {
  const { slug } = useParams<{ slug: string }>()
  const location = useLocation()
  const [state, setState] = useState<PublicSharePageState>(initialiseState)
  const [activeTab, setActiveTab] = useState<'verifications' | 'about' | 'what'>(
    'verifications',
  )

  const token = useMemo<string>(() => {
    // HashRouter exposes the post-hash query as part of location.search
    // already, so URLSearchParams works directly.
    const search = new URLSearchParams(location.search)
    return search.get('k') ?? ''
  }, [location.search])

  useEffect(() => {
    // Public, no-auth demo pass — renders the real share-page UI with
    // fictional data so anyone can preview a QuickPass without an account or
    // token, and without hitting the backend.
    if (slug === 'demo') {
      setState({
        status: 'ready',
        payload: DEMO_SHARE_PAYLOAD,
        errorMessage: '',
      })
      return
    }
    if (slug === undefined || slug === '') {
      setState({ status: 'not-found', payload: null, errorMessage: '' })
      return
    }
    if (token === '') {
      setState({ status: 'invalid-link', payload: null, errorMessage: '' })
      return
    }

    let isMounted = true

    const load = async (): Promise<void> => {
      setState(initialiseState())
      const result = await getPublicQuickPassSharePage(slug, token)
      if (isMounted === false) return

      if (result.kind === 'ok') {
        setState({ status: 'ready', payload: result.payload, errorMessage: '' })
        return
      }
      if (result.kind === 'not-found') {
        setState({ status: 'not-found', payload: null, errorMessage: '' })
        return
      }
      if (result.kind === 'invalid-link') {
        setState({ status: 'invalid-link', payload: null, errorMessage: '' })
        return
      }
      if (result.kind === 'disabled') {
        setState({ status: 'disabled', payload: null, errorMessage: '' })
        return
      }
      setState({
        status: 'error',
        payload: null,
        errorMessage: result.message,
      })
    }

    void load()

    return () => {
      isMounted = false
    }
  }, [slug, token])

  if (state.status === 'loading') {
    return (
      <PageRoot>
        <PageInner>
          <BrandRow>
            <BrandWordmark>QuickPass</BrandWordmark>
            <BrandTagline>Renter pass</BrandTagline>
          </BrandRow>
          <StateCard>
            <StateBody>Loading…</StateBody>
          </StateCard>
        </PageInner>
      </PageRoot>
    )
  }

  if (state.status !== 'ready' || state.payload === null) {
    const messages: Record<
      'not-found' | 'invalid-link' | 'disabled' | 'error',
      { icon: IconDefinition; title: string; body: string }
    > = {
      'not-found': {
        icon: faCircleQuestion,
        title: 'Pass not found',
        body: 'This QuickPass link doesn’t exist. Double-check the URL with the person who sent it.',
      },
      'invalid-link': {
        icon: faLock,
        title: 'This link expired or was revoked',
        body: 'Ask the renter to send you their current QuickPass link. They can regenerate it from their profile.',
      },
      disabled: {
        icon: faMoon,
        title: 'This pass is hidden right now',
        body: 'The renter has turned their QuickPass off temporarily. Reach out to them directly for an update.',
      },
      error: {
        icon: faTriangleExclamation,
        title: 'Something went wrong',
        body:
          state.errorMessage !== ''
            ? state.errorMessage
            : 'Try again in a minute.',
      },
    }

    const messageKey = state.status as
      | 'not-found'
      | 'invalid-link'
      | 'disabled'
      | 'error'
    const message = messages[messageKey]

    return (
      <PageRoot>
        <PageInner>
          <BrandRow>
            <BrandWordmark>QuickPass</BrandWordmark>
            <BrandTagline>Renter pass</BrandTagline>
          </BrandRow>
          <StateCard>
            <StateEmoji aria-hidden="true">
              <Icon icon={message.icon} label="Status" />
            </StateEmoji>
            <StateTitle>{message.title}</StateTitle>
            <StateBody>{message.body}</StateBody>
          </StateCard>
        </PageInner>
      </PageRoot>
    )
  }

  const payload = state.payload
  const initials = buildInitials(payload.displayName)
  const memberSince = formatMemberSince(payload.memberSinceTimestamp)
  const verifiedBadges =
    payload.authProviders !== null && payload.authProviders.length > 0
      ? payload.authProviders.map(provider => ({
          key: provider,
          label: formatAuthProvider(provider),
        }))
      : []
  const aboutMeGroups =
    payload.aboutMeParagraphs !== null
      ? (
          [
            { label: 'About me', text: payload.aboutMeParagraphs.aboutMe },
            {
              label: 'Why I’m a good fit',
              text: payload.aboutMeParagraphs.whyGoodFit,
            },
            {
              label: 'Good to know',
              text: payload.aboutMeParagraphs.additionalNotes,
            },
          ] as const
        ).flatMap(group =>
          group.text !== null && group.text.trim() !== ''
            ? [{ label: group.label, text: group.text.trim() }]
            : [],
        )
      : []

  const verificationProofs: VerificationProof[] = payload.verifications
    .map(verification => getVerificationProof(verification, payload.firstName))
    .filter((proof): proof is VerificationProof => proof !== null)

  const verifierFirstName =
    payload.firstName.trim() !== '' ? payload.firstName.trim() : 'this renter'
  const verifierPossessive =
    payload.firstName.trim() !== ''
      ? `${payload.firstName.trim()}’s`
      : 'their'
  const passNumber = buildPassNumber(
    payload.slug,
    payload.memberSinceTimestamp,
  )

  const hasVerifications = verificationProofs.length > 0
  const hasAbout =
    payload.preferences !== null ||
    aboutMeGroups.length > 0 ||
    payload.contact !== null
  const aboutTabLabel =
    payload.firstName.trim() !== ''
      ? `About ${payload.firstName.trim()}`
      : 'About'
  const tabs: { id: 'verifications' | 'about' | 'what'; label: string }[] = [
    ...(hasVerifications
      ? [{ id: 'verifications' as const, label: 'Verifications' }]
      : []),
    ...(hasAbout ? [{ id: 'about' as const, label: aboutTabLabel }] : []),
    { id: 'what' as const, label: 'What is QuickPass?' },
  ]
  const effectiveTab = tabs.some(tab => tab.id === activeTab)
    ? activeTab
    : tabs[0].id

  const whatPoints = [
    {
      icon: faBuilding,
      title: 'Built by QuickCasa',
      body: 'QuickPass is made by QuickCasa — the platform thousands of property managers and leasing teams already use to run their rentals. The same people who build tools for landlords built the renter side too.',
    },
    {
      icon: faHandshake,
      title: 'Independent, third-party checks',
      body: 'Every verification is run by an accredited outside company: Didit for ID, Plaid for income and funds, Certn for credit and background. QuickPass does not decide who passes; the partners report the result.',
    },
    {
      icon: faLock,
      title: 'QuickPass never sees the sensitive part',
      body: 'ID photos and bank logins go straight to the verification partner over an encrypted connection. QuickPass only ever receives the outcome — never the raw documents, passwords, or account numbers.',
    },
    {
      icon: faShieldCheck,
      title: 'Badges cannot be faked or edited',
      body: 'A renter cannot add, change, or remove a verification. Each badge is written only when a partner confirms the check — so a green checkmark always reflects a real, completed verification, never something typed in.',
    },
    {
      icon: faSliders,
      title: 'The renter stays in control',
      body: `Renters choose which sections appear and can revoke this link at any time. You are seeing exactly what ${verifierFirstName} chose to share — nothing scraped, nothing sold.`,
    },
  ]

  return (
    <PageRoot>
      <PageInner>
        <BrandRow>
          <BrandWordmark>QuickPass</BrandWordmark>
          <BrandTagline>Renter pass</BrandTagline>
        </BrandRow>

        <HeroCard>
          <HeroHeader>
            <HeroBrand>&#10022; QuickPass</HeroBrand>
            <HeroNo>{passNumber}</HeroNo>
          </HeroHeader>
          <HeroBody>
            <HeroTop>
              <Avatar>{initials}</Avatar>
              <HeroText>
                <Name>{payload.displayName}</Name>
                {payload.headline !== '' ? (
                  <Headline>{payload.headline}</Headline>
                ) : payload.city !== '' ? (
                  <Headline>Looking in {payload.city}</Headline>
                ) : null}
              </HeroText>
            </HeroTop>

            {verifiedBadges.length > 0 ||
            verificationProofs.length > 0 ||
            payload.savedCount !== null ? (
              <BadgeRow>
                {verificationProofs.map((proof, index) => (
                  <Badge key={`${proof.badgeLabel}-${index}`}>
                    <Icon icon={faCheck} /> {proof.badgeLabel}
                  </Badge>
                ))}
                {verifiedBadges.map(badge => (
                  <Badge key={badge.key}>
                    <Icon icon={faCheck} /> {badge.label}
                  </Badge>
                ))}
                {payload.savedCount !== null ? (
                  <InfoBadge>
                    <Icon icon={faBookOpen} /> {payload.savedCount} place
                    {payload.savedCount === 1 ? '' : 's'} on QuickPass
                  </InfoBadge>
                ) : null}
                {memberSince !== '' ? (
                  <InfoBadge>
                    <Icon icon={faCalendar} /> Member since {memberSince}
                  </InfoBadge>
                ) : null}
              </BadgeRow>
            ) : null}

            {payload.customMessage !== '' ? (
              <CustomNote>{payload.customMessage}</CustomNote>
            ) : null}
          </HeroBody>
          <Perf />
          <HeroStub>
            <HeroStubRow>
              <span>Admit to every listing</span>
              <span>/me/{payload.slug}</span>
            </HeroStubRow>
            <Barcode aria-hidden="true" />
          </HeroStub>
        </HeroCard>

        {tabs.length > 1 ? (
          <TabBar role="tablist">
            {tabs.map(tab => (
              <Tab
                key={tab.id}
                type="button"
                active={tab.id === effectiveTab}
                aria-selected={tab.id === effectiveTab}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </Tab>
            ))}
          </TabBar>
        ) : null}

        {effectiveTab === 'verifications' && hasVerifications ? (
          <>
            <SectionCard>
              <SectionTitle>
                <Icon icon={faShieldCheck} /> How {verifierFirstName} is verified
            </SectionTitle>
            <VerifyIntro>
              Independent companies confirmed each detail below — QuickPass only
              carries the result. Every check ran directly with the provider, so
              QuickPass never sees {verifierPossessive} documents or banking
              logins.
            </VerifyIntro>
            <VerifyList>
              {verificationProofs.map((proof, index) => {
                const verifiedDate = formatVerifiedDate(proof.verifiedAt)
                return (
                  <VerifyItem key={`${proof.title}-${index}`}>
                    <VerifyHead>
                      <VerifyIcon>
                        <Icon icon={proof.icon} />
                      </VerifyIcon>
                      <VerifyHeadText>
                        <VerifyTitleRow>
                          <VerifyTitle>{proof.title}</VerifyTitle>
                          <VerifyCheck>
                            <Icon icon={faCheck} /> Verified
                            {verifiedDate !== '' ? ` · ${verifiedDate}` : ''}
                          </VerifyCheck>
                        </VerifyTitleRow>
                        <VerifyResult>{proof.result}</VerifyResult>
                      </VerifyHeadText>
                    </VerifyHead>
                    <VerifyProves>{proof.proves}</VerifyProves>
                    <VerifyProvider>
                      <VerifyProviderName>
                        Verified by {proof.providerName}
                      </VerifyProviderName>
                      {proof.providerBlurb !== '' ? (
                        <VerifyProviderBlurb>
                          {proof.providerBlurb}
                        </VerifyProviderBlurb>
                      ) : null}
                    </VerifyProvider>
                    <VerifyMethod>
                      <Icon icon={faLock} /> {proof.method}
                    </VerifyMethod>
                  </VerifyItem>
                )
              })}
            </VerifyList>
            <VerifyPrivacy>
              <Icon icon={faLock} /> These badges can’t be self-edited — each one
              reflects a completed check by the named partner on the date shown.
            </VerifyPrivacy>
          </SectionCard>

            <ConfirmCard>
            <ConfirmTitle>
              <Icon icon={faShieldCheck} /> Don’t take our word for it
            </ConfirmTitle>
            <ConfirmLead>
              We stand behind every check on this pass. Want independent
              confirmation of {verifierPossessive} verifications? Email the
              QuickPass team and a real person will confirm the results above
              directly — you don’t need an account.
            </ConfirmLead>
            <ConfirmSteps>
              <ConfirmStep>
                <ConfirmStepNum>1</ConfirmStepNum>
                <ConfirmStepText>
                  <ConfirmStepTitle>You reach out</ConfirmStepTitle>
                  <ConfirmStepBody>
                    Tell us what you’d like confirmed about this pass.
                  </ConfirmStepBody>
                </ConfirmStepText>
              </ConfirmStep>
              <ConfirmStep>
                <ConfirmStepNum>2</ConfirmStepNum>
                <ConfirmStepText>
                  <ConfirmStepTitle>We pick it up</ConfirmStepTitle>
                  <ConfirmStepBody>
                    A real person replies to let you know we’ve got it.
                  </ConfirmStepBody>
                </ConfirmStepText>
              </ConfirmStep>
              <ConfirmStep>
                <ConfirmStepNum>3</ConfirmStepNum>
                <ConfirmStepText>
                  <ConfirmStepTitle>We ask for the okay</ConfirmStepTitle>
                  <ConfirmStepBody>
                    We never discuss a pass without permission — so we check
                    with {verifierFirstName} first.
                  </ConfirmStepBody>
                </ConfirmStepText>
              </ConfirmStep>
              <ConfirmStep>
                <ConfirmStepNum>4</ConfirmStepNum>
                <ConfirmStepText>
                  <ConfirmStepTitle>We confirm together</ConfirmStepTitle>
                  <ConfirmStepBody>
                    With the green light, we walk you through the verified
                    results.
                  </ConfirmStepBody>
                </ConfirmStepText>
              </ConfirmStep>
            </ConfirmSteps>
            <ConfirmButton
              href={buildVerificationContactHref(
                payload.displayName,
                payload.slug,
              )}
            >
              <Icon icon={faEnvelope} /> Email our verifications team
            </ConfirmButton>
            <ConfirmHint>
              Writes to {VERIFICATIONS_CONTACT_EMAIL} — with this pass already
              referenced so we know who you mean.
            </ConfirmHint>
          </ConfirmCard>
          </>
        ) : null}

        {effectiveTab === 'about' && hasAbout ? (
          <>
        {payload.preferences !== null ? (
          <SectionCard>
            <SectionTitle>What I’m looking for</SectionTitle>
            <StatGrid>
              <StatItem>
                <StatLabel>Cities</StatLabel>
                <StatValue>
                  {payload.preferences.cities.length > 0
                    ? payload.preferences.cities.join(', ')
                    : 'Open to anywhere'}
                </StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Budget</StatLabel>
                <StatValue>{formatBudget(payload.preferences)}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Bedrooms</StatLabel>
                <StatValue>
                  {formatBedrooms(payload.preferences.bedroomCounts)}
                </StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Bathrooms</StatLabel>
                <StatValue>
                  {formatBathrooms(payload.preferences.bathroomCounts)}
                </StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Place type</StatLabel>
                <StatValue>
                  {formatPropertyTypes(payload.preferences.propertyTypes)}
                </StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Whole place or unit</StatLabel>
                <StatValue>
                  {formatDwellingScope(payload.preferences.dwellingScope)}
                </StatValue>
              </StatItem>
              {payload.preferences.preferredMoveInDate !== null &&
              payload.preferences.preferredMoveInDate > 0 ? (
                <StatItem>
                  <StatLabel>Hoping to move</StatLabel>
                  <StatValue>
                    {formatMoveInDate(payload.preferences.preferredMoveInDate)}
                  </StatValue>
                </StatItem>
              ) : null}
              {payload.preferences.monthlyTakeHomeIncome !== null &&
              payload.preferences.monthlyTakeHomeIncome > 0 ? (
                <StatItem>
                  <StatLabel>Monthly take-home</StatLabel>
                  <StatValue>
                    {formatIncome(payload.preferences.monthlyTakeHomeIncome)}
                  </StatValue>
                </StatItem>
              ) : null}
            </StatGrid>
          </SectionCard>
        ) : null}

        {aboutMeGroups.length > 0 ? (
          <SectionCard>
            <SectionTitle>A bit about me</SectionTitle>
            {aboutMeGroups.map(group => (
              <AboutGroup key={group.label}>
                <AboutSubtitle>{group.label}</AboutSubtitle>
                <AboutParagraph>{group.text}</AboutParagraph>
              </AboutGroup>
            ))}
          </SectionCard>
        ) : null}

        {payload.contact !== null ? (
          <SectionCard>
            <SectionTitle>Get in touch</SectionTitle>
            <ContactRow>
              <ContactLine href={`mailto:${payload.contact.email}`}>
                <Icon icon={faEnvelope} /> {payload.contact.email}
              </ContactLine>
              {payload.contact.phone !== null &&
              payload.contact.phone !== '' ? (
                <ContactLine href={`tel:${payload.contact.phone}`}>
                  <Icon icon={faPhone} /> {payload.contact.phone}
                </ContactLine>
              ) : null}
            </ContactRow>
          </SectionCard>
        ) : null}
          </>
        ) : null}

        {effectiveTab === 'what' ? (
          <SectionCard>
            <SectionTitle>
              <Icon icon={faShieldCheck} /> What is QuickPass?
            </SectionTitle>
            <VerifyIntro>
              QuickPass is a free, portable renter identity. A renter builds it
              once — proving who they are and that they can afford a place — then
              shares it with any landlord, like this one. No re-doing the same
              paperwork for every application.
            </VerifyIntro>
            <VerifyList>
              {whatPoints.map(point => (
                <WhatItem key={point.title}>
                  <WhatIcon>
                    <Icon icon={point.icon} />
                  </WhatIcon>
                  <WhatText>
                    <WhatTitle>{point.title}</WhatTitle>
                    <WhatBody>{point.body}</WhatBody>
                  </WhatText>
                </WhatItem>
              ))}
            </VerifyList>
            <WhatCtaRow>
              <WhatCta href="https://app.quickpass.club">
                Get your own free pass <Icon icon={faArrowRight} />
              </WhatCta>
              <WhatCta href="https://quickcasa.ai">
                About QuickCasa <Icon icon={faArrowRight} />
              </WhatCta>
            </WhatCtaRow>
          </SectionCard>
        ) : null}

        <FooterText>
          This pass was shared by {payload.displayName} on QuickPass — the
          portable renter identity. Get yours at{' '}
          <strong>app.quickpass.club</strong>.
        </FooterText>
      </PageInner>
    </PageRoot>
  )
}

export default PublicSharePage
